import * as React from "react";
import { useState } from "react";
import {
  Combobox,
  Option,
  Text,
  makeStyles,
  tokens,
  useId,
} from "@fluentui/react-components";
import { Table24Regular, Document24Regular } from "@fluentui/react-icons";
import { StatusItemDto } from "../../../services/status-items.service";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  label: {
    display: "block",
    marginBottom: tokens.spacingVerticalXS,
  },
  combobox: {
    width: "100%",
  },
  optionContent: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  optionIcon: {
    flexShrink: 0,
  },
});

interface StatusItemSelectorProps {
  statusItems: StatusItemDto[];
  selectedStatusItemIds: number[];
  onStatusItemIdsChange: (ids: number[]) => void;
}

const StatusItemSelector: React.FC<StatusItemSelectorProps> = ({
  statusItems,
  selectedStatusItemIds,
  onStatusItemIdsChange,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const comboboxId = useId("status-item-selector");
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  if (statusItems.length === 0) {
    return null;
  }

  // Filter items based on search query
  const filteredItems = query
    ? statusItems.filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase())
      )
    : statusItems;

  const handleOptionSelect = (
    _event: React.SyntheticEvent,
    data: { selectedOptions: string[] }
  ) => {
    onStatusItemIdsChange(data.selectedOptions.map((id) => parseInt(id, 10)));
  };

  // Build display text for selected items when not focused
  const getDisplayValue = () => {
    if (isFocused) {
      return query;
    }
    if (selectedStatusItemIds.length === 0) {
      return "";
    }
    const selectedNames = statusItems
      .filter((item) => selectedStatusItemIds.includes(item.id))
      .map((item) => item.name);
    return selectedNames.join(", ");
  };

  // Convert selected IDs to string[] for Combobox selectedOptions
  const selectedOptions = selectedStatusItemIds.map((id) => id.toString());

  return (
    <div className={styles.container}>
      <Text
        id={comboboxId}
        className={`${typography.sectionTitle} ${styles.label}`}
      >
        Linked Items (optional)
      </Text>
      <Combobox
        aria-labelledby={comboboxId}
        className={styles.combobox}
        multiselect
        placeholder="Search and select items..."
        selectedOptions={selectedOptions}
        onOptionSelect={handleOptionSelect}
        value={getDisplayValue()}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          setIsFocused(true);
          setQuery("");
        }}
        onBlur={() => {
          setIsFocused(false);
          setQuery("");
        }}
      >
        {filteredItems.map((item) => (
          <Option key={item.id} value={item.id.toString()} text={item.name}>
            <div className={styles.optionContent}>
              {item.sheetId ? (
                <Table24Regular className={styles.optionIcon} style={{ color: tokens.colorPaletteGreenForeground1 }} />
              ) : (
                <Document24Regular className={styles.optionIcon} style={{ color: tokens.colorPaletteBlueForeground2 }} />
              )}
              {item.name}
            </div>
          </Option>
        ))}
        {filteredItems.length === 0 && (
          <Option disabled value="" text={`No items match "${query}"`}>
            No items match "{query}"
          </Option>
        )}
      </Combobox>
    </div>
  );
};

export default StatusItemSelector;
